import {Injectable} from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class HelperService {

  constructor() {
  }

  // Get color variable from SCSS by color name
  getColorVariable(name: string) {
    return getComputedStyle(document.documentElement).getPropertyValue('--ct-' + name);
  }

  // Get color variable RGB from SCSS by color name
  getColorVariableRgb(name: string) {
    return getComputedStyle(document.documentElement).getPropertyValue('--ct-' + name + '-rgb');
  }

  // Create gradient for chart
  createGradientChart(ctx: any, color1: string, color2: string) {

    // Create gradient
    const barBackground = ctx.createLinearGradient(0, 0, 0, 150);
    barBackground.addColorStop(0, this.getColorVariable(color1));
    barBackground.addColorStop(1, 'rgba(' + this.getColorVariableRgb(color2) + ', .35)');

    return barBackground;
  }

  /**
   * Builds a self-contained HTML document for printing or emailing reports.
   * Centrally manages common styles, headers, and metadata formatting.
   */
  buildPdfHtml(content: string, title: string, dateRange: string = '', extraMeta: { label: string, value: string }[] = []): string {
    let cleanContent = (content || '').trim();

    // Strip 'd-none' classes and 'display: none' styles to ensure visibility in PDF/Email
    try {
      cleanContent = cleanContent.replace(/class=\"([^\"]*)\"/g, (match, cls) => {
        const newCls = String(cls).split(/\s+/).filter((c: string) => c && c !== 'd-none' && c !== 'd-print-none' && c !== 'table-responsive').join(' ');
        return `class="${newCls}"`;
      });
      cleanContent = cleanContent.replace(/style=\"([^\"]*)\"/g, (match, style) => {
        const newStyle = String(style)
          .split(';')
          .map(s => s.trim())
          .filter(s => s && !/^display\s*:\s*none\s*$/i.test(s))
          .join('; ');
        return `style="${newStyle}"`;
      });
    } catch { /* no-op fallback */
    }

    const runAt = new Date().toLocaleString();
    const styles = `
      <style>
        @media print {
          @page { size: landscape; margin: 0; }
          body { font-family: Arial, sans-serif; margin: 0; padding: 12mm; color: #111827; }
        }
        body { font-family: Arial, sans-serif; margin: 0; padding: 18px 24px; color: #111827; }
        h1 { font-size: 20px; margin: 0 0 8px; font-weight: 600; text-align: left; }
        .meta-container { font-size: 12px; color: #374151; margin-bottom: 12px; }
        .meta-row { display: block; margin-bottom: 2px; text-align: left; }
        .meta-label { font-weight: bold; margin-right: 4px; }
        .report-card { border: 1px solid #d0d7de; border-radius: 8px; background: #fff; }
        .report-head { border-bottom: 1px solid #d0d7de; padding: 10px 12px; }
        .report-body { padding: 12px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #f6f8fa; border-bottom: 1px solid #d0d7de; padding: 6px 8px; font-size: 12px; }
        td { border-bottom: 1px solid #e5e7eb; padding: 6px 8px; font-size: 12px; }
        .text-end { text-align: right; }
        .text-center { text-align: center; }
        .text-bold { font-weight: bold; }
        .text-danger { color: #dc3545; }
        .text-success { color: #198754; }
      </style>
    `;

    let metaHtml = '<div class="meta-container">';
    if (dateRange) {
      metaHtml += `<div class="meta-row"><span class="meta-label">Period:</span><span>${this.escapeHtml(dateRange)}</span></div>`;
    }
    if (extraMeta && extraMeta.length > 0) {
      extraMeta.forEach(m => {
        if (m.value) {
          metaHtml += `<div class="meta-row"><span class="meta-label">${this.escapeHtml(m.label)}:</span><span>${this.escapeHtml(m.value)}</span></div>`;
        }
      });
    }
    metaHtml += `<div class="meta-row"><span class="meta-label">Run:</span><span>${this.escapeHtml(runAt)}</span></div>`;
    metaHtml += '</div>';

    return `
      <html>
        <head><meta charset="utf-8" />${styles}</head>
        <body>
          <div class="report-card">
            <div class="report-head">
              <h1>${this.escapeHtml(title)}</h1>
              ${metaHtml}
            </div>
            <div class="report-body">${cleanContent}</div>
          </div>
        </body>
      </html>
    `;
  }

  /**
   * Utility to print HTML in a hidden iframe and clean up afterward.
   */
  printHtml(html: string): void {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document || iframe.contentDocument;
    if (!doc) {
      console.error('Could not access iframe document for printing');
      return;
    }

    doc.open();
    doc.write(html);
    doc.close();

    let isPrinting = false;
    const printAndCleanup = () => {
      if (isPrinting) {
        return;
      }
      isPrinting = true;
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch (e) {
        console.error('Print failed', e);
      } finally {
        // Delay removal to ensure print dialog has initialized in all browsers
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 1000);
      }
    };

    // Wait for resources (images, etc) within the iframe to load before printing
    iframe.onload = printAndCleanup;

    // Fallback for browsers where onload might not fire for programmatic content
    setTimeout(printAndCleanup, 500);
  }

  private escapeHtml(unsafe: string): string {
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
